// petconnect-v2/frontend/src/components/features/MapView/MapViewComponent.js
// UPDATED: Popup now has a "View Full Post →" button linking to /post/:id
// KEPT: crossOrigin fix, onerror handler escaping fix

import React, { useState, useEffect, useRef, useCallback } from 'react';

const MapViewComponent = ({ posts = [] }) => {
  const mapRef        = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef    = useRef([]);
  const [mapLoaded, setMapLoaded] = useState(false);

  const initMap = useCallback(() => {
    if (!document.getElementById('leaflet-css')) {
      const cssLink = document.createElement('link');
      cssLink.id = 'leaflet-css';
      cssLink.rel = 'stylesheet';
      cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(cssLink);
    }

    if (!window.L) {
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setMapLoaded(true);
        document.body.appendChild(script);
      }
      return;
    }

    if (!mapRef.current) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = window.L.map(mapRef.current).setView([40.7128, -74.0060], 10);
    mapInstanceRef.current = map;

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    markersRef.current = [];
    const bounds = [];

    posts.forEach(post => {
      if (!post.latitude || !post.longitude) return;

      const markerColor =
        post.category === 'found'    ? '#10b981' :
        post.category === 'missing'  ? '#ef4444' :
        post.category === 'adoption' ? '#ec4899' : '#3b82f6';

      const emoji =
        post.category === 'found'    ? '🟢' :
        post.category === 'missing'  ? '🔴' :
        post.category === 'adoption' ? '❤️' : '📍';

      const icon = window.L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color:${markerColor};width:40px;height:40px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:20px;cursor:pointer;">${emoji}</div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
      });

      // Image HTML — no crossorigin to avoid CORS issues on S3/CDN
      const imageUrl = post.media_urls && post.media_urls.length > 0 ? post.media_urls[0] : null;
      const imgId    = `popup-img-${post.id}`;

      const imageHtml = imageUrl
        ? `<div style="margin-top:8px;width:100%;height:140px;overflow:hidden;border-radius:6px;background:#f3f4f6;">
             <img
               id="${imgId}"
               src="${imageUrl}"
               alt="Pet photo"
               style="width:100%;height:140px;object-fit:cover;border-radius:6px;display:block;"
               onerror="this.style.display='none';this.parentElement.innerHTML='<div style=&quot;width:100%;height:140px;background:#f3f4f6;border-radius:6px;display:flex;align-items:center;justify-content:center;font-size:32px;&quot;>🐾</div>';"
             />
           </div>`
        : '';

      // Post detail URL — React Router client-side link
      const postUrl = `/post/${post.id}`;

      const popupContent = `
        <div style="min-width:240px;max-width:280px;font-family:-apple-system,sans-serif;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <span style="font-size:11px;font-weight:700;color:#fff;background:${markerColor};padding:3px 8px;border-radius:20px;text-transform:uppercase;">
              ${post.category}
            </span>
            ${post.status === 'resolved' ? '<span style="font-size:11px;font-weight:700;color:#059669;background:#d1fae5;padding:3px 8px;border-radius:20px;">✓ Reunited</span>' : ''}
          </div>

          <h4 style="margin:0 0 4px 0;font-weight:700;color:#1f2937;font-size:15px;line-height:1.3;">
            ${post.pet_name || 'Unknown Pet'}
          </h4>

          ${post.pet_type ? `<p style="margin:0 0 6px 0;font-size:12px;color:#6b7280;">${post.pet_type}${post.breed && post.pet_type === 'dog' ? ' · ' + post.breed : ''}</p>` : ''}

          <p style="margin:6px 0;font-size:13px;color:#374151;line-height:1.5;">
            ${(post.content || '').substring(0, 100)}${(post.content || '').length > 100 ? '...' : ''}
          </p>

          ${imageHtml}

          ${post.location_name ? `<p style="margin:8px 0 4px;font-size:12px;color:#6b7280;">📍 ${post.location_name}</p>` : ''}
          ${post.contact_info  ? `<p style="margin:4px 0;font-size:12px;color:#2563eb;font-weight:600;">📞 ${post.contact_info}</p>` : ''}

          <p style="margin:6px 0 0;font-size:11px;color:#9ca3af;">
            Posted by ${post.author?.username || 'Unknown'} · ${new Date(post.created_at).toLocaleDateString()}
          </p>

          <a href="${postUrl}"
             style="display:block;margin-top:10px;padding:8px 12px;background:linear-gradient(135deg,#7c3aed,#db2777);color:white;border-radius:8px;text-align:center;font-size:12px;font-weight:700;text-decoration:none;"
             onclick="event.stopPropagation();">
            View Full Post →
          </a>
        </div>
      `;

      const marker = window.L.marker([post.latitude, post.longitude], { icon })
        .bindPopup(popupContent, { maxWidth: 300, className: 'pet-popup' })
        .addTo(map);

      markersRef.current.push(marker);
      bounds.push([post.latitude, post.longitude]);
    });

    if (bounds.length > 0) {
      try {
        map.fitBounds(window.L.latLngBounds(bounds), { padding: [50, 50], maxZoom: 14 });
      } catch (e) {
        console.error('fitBounds error:', e);
      }
    }
  }, [posts]);

  useEffect(() => {
    if (mapLoaded && mapRef.current) initMap();
  }, [mapLoaded, initMap]);

  useEffect(() => {
    if (window.L) {
      initMap();
    } else {
      if (!document.getElementById('leaflet-css')) {
        const cssLink = document.createElement('link');
        cssLink.id = 'leaflet-css';
        cssLink.rel = 'stylesheet';
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(cssLink);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script');
        script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setMapLoaded(true);
        document.body.appendChild(script);
      } else {
        const checkLoaded = setInterval(() => {
          if (window.L) { clearInterval(checkLoaded); setMapLoaded(true); }
        }, 100);
      }
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div
      ref={mapRef}
      style={{ height: '600px', width: '100%' }}
      className="rounded-lg border-2 border-gray-300 shadow-md"
    />
  );
};

export default MapViewComponent;